import type { ZxcvbnFactory } from '@zxcvbn-ts/core';

export interface PasswordCheckResult {
  quality: number;
}

const loadPasswordStrength = async () => {
  const [{ ZxcvbnFactory }, common, en] = await Promise.all([
    import('@zxcvbn-ts/core'),
    import('@zxcvbn-ts/language-common'),
    import('@zxcvbn-ts/language-en'),
  ]);

  return new ZxcvbnFactory({
    dictionary: {
      ...common.dictionary,
      ...en.dictionary,
    },
    graphs: common.adjacencyGraphs,
  });
};

let passwordStrength: Promise<ZxcvbnFactory> | undefined;

const SEPARATORS = ['', '.', '-', '_'];

const toUserTokens = (userInputs: Nullable<string | undefined>[]) => {
  const inputs = userInputs.filter((input): input is string => !!input);
  const tokens = new Set(inputs);

  for (const input of inputs) {
    const words = [...input.matchAll(/[\p{L}\p{N}]+/gu)];

    words.forEach((firstWord, first) => {
      words.slice(first).forEach((lastWord, offset) => {
        const span = words
          .slice(first, first + offset + 1)
          .map(([word]) => word);

        tokens.add(
          input.slice(firstWord.index, lastWord.index + lastWord[0].length)
        );
        SEPARATORS.forEach((separator) => tokens.add(span.join(separator)));
      });
    });
  }

  for (const first of inputs) {
    for (const second of inputs) {
      if (first !== second) {
        SEPARATORS.forEach((separator) =>
          tokens.add(`${first}${separator}${second}`)
        );
      }
    }
  }

  return [...tokens].filter((token) => token.length >= 3);
};

export const checkPassword = async ({
  password,
  minLength,
  userInputs = [],
}: {
  password: string;
  minLength: number;
  userInputs?: Nullable<string | undefined>[];
}): Promise<PasswordCheckResult> => {
  if (!password || password.length < minLength) {
    return { quality: 0 };
  }

  passwordStrength ??= loadPasswordStrength().catch((error: unknown) => {
    passwordStrength = undefined;
    throw error;
  });

  const { score } = (await passwordStrength).check(
    password,
    toUserTokens(userInputs)
  );

  return { quality: score + 1 };
};

export const isValidPasswordForm = ({
  quality,
  minQuality,
  oldPassword,
  newPassword,
  repeatedPassword,
}: {
  quality: number;
  minQuality: number;
  oldPassword: string;
  newPassword: string;
  repeatedPassword: string;
}) =>
  quality - 1 >= minQuality &&
  oldPassword.length > 0 &&
  newPassword === repeatedPassword;
